import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { AnyVariables, CombinedError, UseQueryArgs } from 'urql';

import { useQueryStable } from '@/hooks/useQueryStable';
import { Prettify } from '@/util/type';

interface PaginatedQueryContext<TData> {
  after: string | undefined;
  allResults: TData[];
}

const paginationInitialState: PaginatedQueryContext<any> = {
  after: undefined,
  allResults: [],
};

type PaginationAction<TData = any> =
  | {
      type: 'nextPage';
      payload: {
        after: string;
      };
    }
  | {
      type: 'reset';
    }
  | { type: 'updateResults'; payload: TData[] };
// type PaginationActionType = PaginationAction["type"];

function resultsCacheReducer<TData>(
  state: PaginatedQueryContext<TData>,
  action: PaginationAction<TData>,
) {
  const updateResultsHandler = (newResults: TData[]) => {
    const updatedResults = [...state.allResults, ...newResults];
    return {
      ...state,
      allResults: updatedResults,
    };
  };

  switch (action.type) {
    case 'nextPage':
      return {
        ...state,
        after: action.payload.after,
      };

    case 'reset':
      return paginationInitialState as PaginatedQueryContext<TData>;

    case 'updateResults':
      return updateResultsHandler(action.payload);

    default:
      return state;
  }
}

export type PageInfo<TMetadata = never> = {
  totalCount: number | null;
  endCursor: string | null;
  hasNextPage: boolean;
  metadata?: TMetadata;
};

export type TPaginationVars = Prettify<
  AnyVariables & {
    after: string | undefined;
  }
>;

export type PaginatedQueryResult<TData, TMetadata> = {
  fetching: boolean;
  error: CombinedError | undefined;
  data: TData[] | undefined;
  pageInfo: PageInfo<TMetadata> | undefined;
  fetchMore: () => void;
};

export const usePaginatedQuery = <
  TData,
  TMetadata,
  TResult,
  TVariables extends TPaginationVars = TPaginationVars,
>(
  options: UseQueryArgs<TVariables, TResult>,
  getPageInfo: (data: NonNullable<TResult>) => PageInfo<TMetadata> | undefined,
  getResults: (data: NonNullable<TResult>) => TData[] | undefined,
  getLastUpdatedAt: (data: NonNullable<TResult>) => string | null | undefined,
) => {
  const getPageInfoRef = useRef(getPageInfo);
  getPageInfoRef.current = getPageInfo;

  const getResultsRef = useRef(getResults);
  getResultsRef.current = getResults;

  const getLastUpdatedAtRef = useRef(getLastUpdatedAt);
  getLastUpdatedAtRef.current = getLastUpdatedAt;

  const [resultsCache, dispatch] = useReducer(
    resultsCacheReducer<TData>,
    paginationInitialState,
  );
  const cacheKey = JSON.stringify(options.variables);

  const variables = {
    ...options.variables,
    after: resultsCache.after,
  };

  const [{ fetching, error, data: currentPageData }] = useQueryStable({
    ...options,
    variables,
  });

  const currentResults = useMemo(
    () => (currentPageData ? getResultsRef.current(currentPageData) : undefined),
    [currentPageData],
  );

  const pageInfo = useMemo(() => {
    if (!currentPageData) return undefined;
    return getPageInfoRef.current(currentPageData);
  }, [currentPageData]);

  const fetchMore = useCallback(() => {
    if (!pageInfo || !pageInfo.endCursor || !pageInfo.hasNextPage) return;
    dispatch({
      type: 'nextPage',
      payload: { after: pageInfo.endCursor },
    });
  }, [pageInfo]);

  const resetCache = () => {
    dispatch({ type: 'reset' });
  };

  const mergeResults = () => {
    if (!currentResults) {
      return;
    }
    dispatch({ type: 'updateResults', payload: currentResults });
  };

  useEffect(resetCache, [cacheKey]);
  useEffect(mergeResults, [currentResults]);

  // first page SSR workaround (since results map is only set on useEffect)
  const results =
    resultsCache.allResults.length === 0 ? currentResults : resultsCache.allResults;

  return useMemo(
    () => ({
      fetching,
      error,
      data: results,
      lastUpdatedAt: currentPageData
        ? getLastUpdatedAtRef.current(currentPageData)
        : undefined,
      pageInfo,
      fetchMore,
    }),
    [currentPageData, error, fetchMore, fetching, pageInfo, results],
  );
};
